import React, {
  FC, Fragment, useEffect, useState
} from 'react';
import SvgIcon from 'src/react_components/svg-icon';

type Partner = {
  clientName: string;
  slug: string;
  logo: string | null;
  url: string;
};

const PartnerCarousel: FC<{ partners: Map<number, Partner[]> }> = ({ partners }) => {
  const carouselRef = React.useRef<HTMLDivElement>(null);
  const autoRotateRef = React.useRef<NodeJS.Timeout | null>(null);

  const [carouselWidth, setCarouselWidth] = useState(0);
  const [columns, setColumns] = useState(4);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const [horizontalOffset, setHorizontalOffset] = useState(0);
  const [disableTransition, setDisableTransition] = useState(false);

  const rows = 2;
  const gap = 24;
  const minColumnWidth = 150;
  const columnWidth = minColumnWidth + gap;
  const itemsPerPage = columns * rows;
  const slidesToScroll = columns;
  const partnerList = partners.get(1) || [];
  const totalColumns = Math.ceil(partnerList.length / rows);
  const maxHorizontalOffset = Math.max(0, totalColumns - columns);

  // Show controls only if there are more partners than fit on screen
  const showControls = maxHorizontalOffset > 0;

  const startAutoRotate = () => {
    const carousel = carouselRef?.current;
    if (!carousel || maxHorizontalOffset === 0) return;

    const bsCarousel = window.bootstrap?.Carousel?.getOrCreateInstance(carousel);

    autoRotateRef.current = setInterval(() => {
      setHorizontalOffset((prev) => {
        if (prev >= maxHorizontalOffset) {
          // Already at end, reset to start and move to next Bootstrap slide
          if (bsCarousel) {
            setDisableTransition(true);
            setHorizontalOffset(0);
            requestAnimationFrame(() => {
              setDisableTransition(false);
              bsCarousel.next();
            });
          }
          return 0;
        }
        // Advance offset, snap to max if we'd overshoot
        const next = prev + slidesToScroll;
        return Math.min(next, maxHorizontalOffset);
      });
    }, 5000);
  };

  const resetInterval = () => {
    if (autoRotateRef.current) {
      clearInterval(autoRotateRef.current);
      autoRotateRef.current = null;
    }
    startAutoRotate();
  };

  useEffect(() => {
    const carousel = carouselRef?.current;
    if (!carousel) return undefined;

    // Initial check
    getGridColumns();

    // Initialize Bootstrap carousel without auto-rotation
    window.bootstrap?.Carousel?.getOrCreateInstance(carousel, {
      interval: false,
      ride: false,
      wrap: true
    });

    // Start custom auto-rotation
    startAutoRotate();

    // Re-check on resize
    const handleResize = () => {
      getGridColumns();
      setHorizontalOffset(0);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      if (autoRotateRef.current) clearInterval(autoRotateRef.current);
      window.removeEventListener('resize', handleResize);
    };
  }, [columns, partnerList.length, itemsPerPage, maxHorizontalOffset]);

  const handleImageLoad = (slug: string) => {
    setLoadedImages((prev) => new Set(prev).add(slug));
  };

  const getColumnsFromWidth =
      (containerWidth: number) => Math.max(1, Math.floor((containerWidth + gap) / (minColumnWidth + gap)));

  const getGridColumns = () => {
    const carousel = carouselRef?.current;
    if (carousel) {
      const containerWidth = carousel.clientWidth;
      const availableWidth = Math.min(
        carousel.parentElement?.clientWidth || containerWidth,
        document.documentElement.clientWidth
      );
      const calculatedColumns = getColumnsFromWidth(availableWidth);

      // If columns changed, reset to first slide
      if (calculatedColumns !== columns) {
        if (window.bootstrap?.Carousel) {
          const bsCarousel = window.bootstrap.Carousel.getInstance(carousel);
          if (bsCarousel) {
            bsCarousel.to(0);
          }
        }
      }

      setColumns(calculatedColumns);

      // Set the carousel width to prevent layout shifts when images load
      const calculatedCarouselWidth = calculatedColumns * (minColumnWidth + gap) - gap;
      setCarouselWidth(calculatedCarouselWidth);
    }
  };

  const spliceIntoChunks = (arr: Partner[], chunkSize: number) => {
    const res: Partner[][] = [];
    const arrCopy = [...arr];
    while (arrCopy.length > 0) {
      const chunk = arrCopy.splice(0, chunkSize);
      res.push(chunk);
    }

    return res;
  };

  const handlePrevSlide = () => {
    resetInterval();
    if (horizontalOffset >= slidesToScroll) {
      // Scroll horizontally within slide
      setHorizontalOffset((prev) => prev - slidesToScroll);
    } else if (horizontalOffset > 0) {
      // Snap to start
      setHorizontalOffset(0);
    } else {
      // At start, go to previous Bootstrap slide and reset to end
      const carousel = carouselRef?.current;
      if (carousel && window.bootstrap?.Carousel) {
        const bsCarousel = window.bootstrap.Carousel.getOrCreateInstance(carousel);
        setDisableTransition(true);
        setHorizontalOffset(maxHorizontalOffset);
        requestAnimationFrame(() => {
          setDisableTransition(false);
          bsCarousel.prev();
        });
      }
    }
  };

  const handleNextSlide = () => {
    resetInterval();
    const newOffset = horizontalOffset + slidesToScroll;
    if (newOffset < maxHorizontalOffset) {
      // Scroll horizontally within slide
      setHorizontalOffset(newOffset);
    } else if (horizontalOffset < maxHorizontalOffset) {
      // Snap to end
      setHorizontalOffset(maxHorizontalOffset);
    } else {
      // At end, go to next Bootstrap slide and reset to start
      const carousel = carouselRef?.current;
      if (carousel && window.bootstrap?.Carousel) {
        const bsCarousel = window.bootstrap.Carousel.getOrCreateInstance(carousel);
        setDisableTransition(true);
        setHorizontalOffset(0);
        requestAnimationFrame(() => {
          setDisableTransition(false);
          bsCarousel.next();
        });
      }
    }
  };

  return (
    <div
      ref={carouselRef}
      id="partner-carousel"
      className="carousel slide"
      data-bs-ride="carousel"
    >
      <div className="carousel-inner">
        {partnerList.length > 0 && [0, 1, 2].map((slideIndex) => (
          <div
            key={`slide-${slideIndex}`}
            className={slideIndex === 1 ? 'carousel-item active' : 'carousel-item'}
          >
            <div
              className="overflow-hidden"
              style={{ width: '100%', maxWidth: carouselWidth ? `${carouselWidth}px` : '100%', margin: '0 auto' }}
            >
              <div
                className="d-flex gap-4"
                style={{
                  transform: `translateX(-${horizontalOffset * columnWidth}px)`,
                  transition: disableTransition ? 'none' : 'transform 0.5s ease-in-out'
                }}
              >
                {spliceIntoChunks(partnerList, rows).map((column, colIndex) => (
                  <div
                    key={column[0]?.slug || `col-${slideIndex}-${colIndex}`}
                    className="partner-column d-flex flex-column gap-4"
                    style={{ minWidth: `${minColumnWidth}px` }}
                  >
                    {column.filter(Boolean).map((partner: Partner) => (
                      <a
                        key={partner.slug}
                        href={partner.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {partner.logo ? (
                          <Fragment>
                            <img
                              src={partner.logo}
                              alt={`${partner.clientName} logo`}
                              className="img-fluid"
                              onLoad={() => handleImageLoad(partner.slug)}
                            />
                            {!loadedImages.has(partner.slug) && (
                              <SvgIcon icon="spinner" className="icon-spin" />
                            )}
                          </Fragment>
                        ) : (
                          <span>
                            {partner.clientName}
                          </span>
                        )}
                      </a>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {showControls && (
        <Fragment>
          <button
            className="carousel-control-prev position-absolute top-50 start-0"
            type="button"
            onClick={handlePrevSlide}
            aria-label="Previous"
          >
            <SvgIcon icon="chevron-left" className="m-0 text-body" />
          </button>
          <button
            className="carousel-control-next position-absolute top-50 end-0"
            type="button"
            onClick={handleNextSlide}
            aria-label="Next"
          >
            <SvgIcon icon="chevron-right" className="m-0 text-body" />
          </button>
        </Fragment>
      )}
    </div>
  );
};
export default PartnerCarousel;
